import { useCallback, useEffect, useMemo, useState } from "react";
import { Feature, FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import { useColorModeValue, theme } from "@chakra-ui/react";
import { Layer, Source, useMap } from "react-map-gl";
import Tilebelt from "tilebelt";
import { bbox } from "@turf/bbox";
import rbush from "@turf/geojson-rbush";

type Props = {
  data?: FeatureCollection,
  beforeId?: string,
  showGridLabels?: boolean,
  hoveredSceneId?: string | null,
}

function MapGrid({ data, beforeId, showGridLabels, hoveredSceneId }: Props) {
  const {current: map} = useMap();
  const [grid, setGrid] = useState<FeatureCollection>();
  const [footprintsTree, setFootprintsTree] = useState<ReturnType<typeof rbush>>();
  const gridLineColor = useColorModeValue(theme.colors.blackAlpha["50"], theme.colors.whiteAlpha["50"]);
  const gridHoverColor = useColorModeValue(theme.colors.cyan["600"], theme.colors.cyan["400"]);
  // Creates a GeoJSON collection with the grid for the current zoom level
  const makeGrid = useCallback(() => {
    if (!map) return;

    const bounds = map.getBounds().toArray().flat();
    const mapZoom = map.getZoom();

    if (!mapZoom || !bounds) return;

    // Cap how fine the grid gets: past zoom 8, keep showing the zoom-8 grid
    // re-fit to the current viewport, instead of an ever-finer (and slower) one.
    const zoom = Math.min(mapZoom, 8) + 4;

    const ll = Tilebelt.pointToTile(bounds[0], bounds[1], zoom);
    const ur = Tilebelt.pointToTile(bounds[2], bounds[3], zoom);

    const boxes = [];
    for (let x = ll[0]; x <= ur[0]; x++) {
      for (let y = ll[1]; y >= ur[1]; y--) {
        const tile = [x, y, zoom];
        const feature: Feature = {
          type: "Feature",
          properties: {
            _quadKey: Tilebelt.tileToQuadkey(tile),
            id: boxes.length,
            tile: tile.join("/")
          },
          geometry: Tilebelt.tileToGeoJSON(tile)
        };
        boxes.push(feature);
      }
    }

    setGrid({
      type: "FeatureCollection",
      features: boxes
    });
  }, [map]);

  // Register event handlers to update the grid when the map extent changes
  useEffect(() => {
    makeGrid();
    map?.on("dragend", makeGrid);
    map?.on("zoomend", makeGrid);

    return () => {
      map?.off("dragend", makeGrid);
      map?.off("zoomend", makeGrid);
    };
  }, [makeGrid, map]);

  // Build the RTree for the data, when the data changes
  useEffect(() => {
    if (!data) return;
    const tree = rbush();
    tree.load(data);
    setFootprintsTree(tree);
  }, [data]);

  // Make the grid data. Take the current grid, and get the number of scene intersections with each grid cell
  const gridData = useMemo((): FeatureCollection<Geometry, GeoJsonProperties> | undefined => {
    if (!grid || !footprintsTree) return;

    const features = grid.features.map((f) => {
      const matches = footprintsTree.search(bbox(f)).features.length;
      const sceneIDs = footprintsTree.search(bbox(f)).features.map((scene) => scene?.properties?.s3_prefix);

      return {
        ...f,
        properties: {
          ...f.properties,
          matches,
          sceneIDs
        }
      };
    });

    return {
      type: "FeatureCollection",
      features
    };
  }, [grid, footprintsTree]);

  if (gridData) {
    return (
      <Source
        id="grid"
        type="geojson"
        data={gridData}
        promoteId="id"
      >
        <Layer
          id="grid-fill"
          beforeId={beforeId}
          type="fill"
          paint={{
            "fill-color": {
              "property": "matches",
              "stops": [
                [1, "rgba(2, 194, 235, 0)"],
                [2, "rgba(2, 194, 235, .1)"],
                [3, "rgba(2, 194, 235, .2)"],
                [4, "rgba(2, 194, 235, .3)"],
                [5, "rgba(2, 194, 235, .4)"],
                [6, "rgba(2, 194, 235, .5)"],
                [7, "rgba(2, 194, 235, .6)"],
                [8, "rgba(2, 194, 235, .7)"],
                [9, "rgba(2, 194, 235, .8)"],
                [10, "rgba(2, 194, 235, .9)"]
              ]
            }}}
        />
        <Layer
          id="grid-outline"
          type="line"
          paint={{
            "line-color": [
              "case",
              ["any",
                ["all",
                  ["boolean", ["feature-state", "hover"], false],
                  [">", ["get", "matches"], 0]
                ],
                hoveredSceneId !== undefined ? ["in", hoveredSceneId, ["get", "sceneIDs"]] : false
              ],
              gridHoverColor,
              gridLineColor
            ],
            "line-width": 1
          }}
        />
        {showGridLabels && (
          <Layer
            id="grid-label"
            type="symbol"
            filter={[">", "matches", 0]}
            layout={{
              "text-field": [
                "format",
                ["get", "matches"],
                { "text-size": 12, "text-color": "#fff", "font-scale": 0.6 },
              ],
              "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
            }}
          />
        )}
      </Source>
    );
  }

  return null;
}

export default MapGrid;
